<script setup lang="ts">
const { state, close, next, prev } = useLightbox();

const hasMultiple = computed(() => state.images.length > 1);

function onKey(e: KeyboardEvent) {
  if (!state.isOpen) return;
  if (e.key === 'Escape') close();
  else if (e.key === 'ArrowRight') next();
  else if (e.key === 'ArrowLeft') prev();
}

// Lock background scroll while the lightbox is open.
watch(() => state.isOpen, (open) => {
  if (typeof document === 'undefined') return;
  document.body.style.overflow = open ? 'hidden' : '';
});

onMounted(() => window.addEventListener('keydown', onKey));
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey);
  if (typeof document !== 'undefined') document.body.style.overflow = '';
});
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="lb-fade">
        <div
          v-if="state.isOpen"
          class="fixed inset-0 z-[100] flex items-center justify-center bg-stone-950/90 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          @click.self="close">

          <!-- Close -->
          <button
            type="button"
            class="absolute top-4 right-4 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Close"
            @click="close">
            <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <!-- Prev -->
          <button
            v-if="hasMultiple"
            type="button"
            class="absolute left-3 sm:left-5 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Previous"
            @click.stop="prev">
            <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <!-- Image -->
          <Transition name="lb-img" mode="out-in">
            <img
              :key="state.images[state.index]"
              :src="state.images[state.index]"
              class="max-h-[85vh] max-w-[92vw] rounded-xl object-contain shadow-2xl"
              alt="" />
          </Transition>

          <!-- Next -->
          <button
            v-if="hasMultiple"
            type="button"
            class="absolute right-3 sm:right-5 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Next"
            @click.stop="next">
            <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          <!-- Counter -->
          <div
            v-if="hasMultiple"
            class="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-white">
            {{ state.index + 1 }} / {{ state.images.length }}
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style scoped>
.lb-fade-enter-active,
.lb-fade-leave-active {
  transition: opacity 0.25s ease;
}
.lb-fade-enter-from,
.lb-fade-leave-to {
  opacity: 0;
}

.lb-img-enter-active,
.lb-img-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.lb-img-enter-from,
.lb-img-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
</style>
